import './dyf-landing-view.scss';
import { useEffect, useState } from 'react';
import { DyfFeatureGuideContentProps } from './hooks/use-dyf-feature-guide-content';
import { EligibleModelData } from '../../hooks/dyf/use-dyf-eligible-model/use-dyf-eligible-model';
import { FeatureGuideBillboardLincoln } from '../../components/sections/dyf-landing/feature-guide-billboard/feature-guide-billboard.lincoln';
import { CardFiftyFifty } from '@own/fds-react';
import { ShortcodeProviders } from '../../services/shortcode-service/shortcode-service';
import { BlackLabel } from '../../components/sections/dyf-landing/dyl-black-label/dyl-black-label';
import { useDylLandingContent } from './hooks/use-dyl-landing';
import { useDylLandingDynamicContent } from './hooks/use-dyl-landing-dynamic-content';
import { HowToVideoLibrary } from '../../components/sections/dyf-landing/dyl-how-to-video-library/dyl-how-to-video-library';
import { NewAnalyticsService } from '../../services/new-analytics-service/new-analytics-service';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import { VehicleSelector } from '../../components/sections/vehicle-portal/components/vehicle-selector/vehicle-selector';
import { StringUtil } from '../../components/utils/string-util/string-util';
import useSeoConfig from '../../hooks/use-seo-config';
import { VehicleYmm } from '../../models/vehicle-ymm';
import TrademarkSymbolUtil from '../../components/utils/trademark-symbol-util/trademark-symbol-util';
import { DyfFeatureGuideBreadcrumbAemContent } from './hooks/use-dyf-feature-guide-breadcrumbs-content';
import { DyfFeatureGuideBreadCrumbs } from '../../components/sections/dyf-landing/dyf-feature-guide-breadcrumbs/dyf-feature-guide-breadcrumbs';

type DYFFeatureGuideLandingFlow = 'ymm' | 'vin';

interface DyfLandingLincolnComponentProps {
    vehicleYmmGroup: VehicleYmm | null;
    featureGuideContent: DyfFeatureGuideContentProps | null;
    eligibleModelData: EligibleModelData | null;
    vin: string;
    isAuthenticated: boolean;
    baseRoot: string;
    vehicleAttributes: VehicleYmm;
    flow: DYFFeatureGuideLandingFlow;
    handleVehicleSelectorOnChange: (
        title: string,
        index: number,
        value: string,
        match: VehicleAttributes
    ) => void;
    fireEvents: (
        eventNames: string | string[],
        targetViewName?: string,
        shortcodeProviders?: ShortcodeProviders,
        clearDigitaldata?: boolean,
        skipFireTargetAnalytics?: boolean
    ) => void;
    dyfLandingBreadcrumbs: DyfFeatureGuideBreadcrumbAemContent;
}

export const DyfLandingViewLincoln = (
    props: DyfLandingLincolnComponentProps
) => {
    const {
        fireEvents,
        featureGuideContent,
        eligibleModelData,
        vin,
        isAuthenticated,
        baseRoot,
        vehicleAttributes,
        handleVehicleSelectorOnChange,
        dyfLandingBreadcrumbs,
        vehicleYmmGroup,
        flow,
    } = props;

    const replaceYMM = (data: string) => {
        return data
            ?.replace('[year]', props?.vehicleYmmGroup?.year.toString() || '')
            .replace('[make]', props?.vehicleYmmGroup?.make || '')
            ?.replace(
                '[model]',
                StringUtil.capitalizeFirstLetter(vehicleYmmGroup?.model || '')
            );
    };

    const dylLanding = useDylLandingContent();
    const dylLandingDynamic = useDylLandingDynamicContent(vehicleAttributes);
    const aemBaseUrl = process.env.REACT_APP_AEM_BASE_URL;
    const handleOnclick = (ctaName: string | undefined) => {
        if (dylLanding) {
            let eventName = '';
            if (vin) {
                eventName = 'dyl-5050-card-vin-cta-onclick-event';
            } else {
                eventName = 'dyl-5050-card-ymm-cta-onclick-event';
            }
            switch (ctaName) {
                case dylLandingDynamic?.third5050CardTitle: //owners manual
                    fireEvents(eventName, undefined, {
                        dylCtaName: 'owners manual',
                    });
                    window.location.href = `${baseRoot}/vehicle/${vehicleAttributes?.seoKey}/${vehicleAttributes?.year}/owner-manuals/`;

                    break;
                case dylLanding.second5050CardTitle: // access rewards
                    fireEvents(eventName, undefined, {
                        dylCtaName: 'lincoln access rewards',
                    });
                    window.location.href = dylLanding.second5050CardCtaLink;

                    break;
            }
        }
    };

    useSeoConfig(
        eligibleModelData ? 'dyl-landing-page' : 'dyl-not-available-page',
        'dyf'
    );

    const metaDescriptionValue = useSeoConfig(
        eligibleModelData ? 'dyl-landing-page' : 'dyl-not-available-page',
        'dyf'
    )?.metaDescription;
    const metaDescriptionTag = document.querySelector(
        'meta[name="description"]'
    );
    metaDescriptionTag &&
        metaDescriptionTag.setAttribute('content', metaDescriptionValue || '');

    const [dylLoadAnalyticFired, setDylLoadAnalyticFired] = useState<boolean>(
        false
    );

    useEffect(() => {
        if (!dylLoadAnalyticFired) {
            handleFeatureGuideAnalyticsDyl(vehicleAttributes);
        }
    }, [vehicleAttributes, dylLoadAnalyticFired]);

    function handleFeatureGuideAnalyticsDyl(ymm: VehicleYmm) {
        let eventName = '';

        if (eligibleModelData) {
            if (flow == 'vin') {
                eventName = 'dyl-landing-vin';
            } else {
                eventName = 'dyl-landing-ymm';
            }
        } else {
            eventName = 'dyl-landing-vehicle-not-available';
        }

        fireEvents(eventName, undefined, { ymm });
        setDylLoadAnalyticFired(true);
    }

    return (
        <div data-testid={'dyf-landing-view-lincoln'}>
            {vehicleAttributes && isAuthenticated && (
                <VehicleSelector
                    disableDashboardRedirect={true}
                    onChange={handleVehicleSelectorOnChange}
                />
            )}
            {dyfLandingBreadcrumbs && (
                <div
                    className="dyf-landing-page-breadcrumbs"
                    data-testid="dyf-landing-page-breadcrumbs"
                >
                    <DyfFeatureGuideBreadCrumbs
                        origin={'landing'}
                        dyfFeatureGuideBreadcrumbs={dyfLandingBreadcrumbs}
                        vinDetails={
                            flow == 'vin'
                                ? {
                                      vehicleName: vehicleYmmGroup?.model || '',
                                      vin: vin!,
                                  }
                                : null
                        }
                        ymmDetails={
                            flow == 'ymm'
                                ? {
                                      modelName:
                                          eligibleModelData?.modelDisplayName ||
                                          vehicleYmmGroup?.model ||
                                          '',
                                  }
                                : null
                        }
                    />
                </div>
            )}
            {vehicleAttributes && featureGuideContent && (
                <div
                    className={`feature-guide-billboard feature-guide-billboard--${flow}`}
                    data-testid="dyf-billboard-lincoln"
                >
                    <FeatureGuideBillboardLincoln
                        eligibleFeatureGuide={
                            eligibleModelData?.eligibleFeatureGuide
                        }
                        vin={vin}
                        vehicleYmmGroup={vehicleAttributes}
                        featureGuideContent={featureGuideContent}
                        isAuthenticated={isAuthenticated}
                        dylLandingContent={dylLanding}
                        hasEligibleModelData={!!eligibleModelData}
                        flow={flow}
                    />
                </div>
            )}

            {/* Tailored Immersion */}
            {eligibleModelData?.eligibleDylTailoredImmersion &&
                dylLanding &&
                dylLanding.first5050CardTitle && (
                    <div className="dyl-card-5050">
                        <CardFiftyFifty
                            title={TrademarkSymbolUtil.replaceTrademarks(
                                dylLanding.first5050CardTitle
                            )}
                            body={TrademarkSymbolUtil.replaceTrademarks(
                                dylLanding.first5050CardBody
                            )}
                            media={{
                                type: 'image',
                                width: 'wide',
                                src:
                                    aemBaseUrl +
                                    replaceYMM(dylLanding.first5050CardImage),
                                alt: replaceYMM(
                                    dylLanding.first5050CardImageAltText
                                ),
                            }}
                            button={{
                                type: 'standard',
                                label: dylLanding.first5050CardCtaLabel,
                                outlined: false,
                                chevron: 'right',
                                disabled: false,
                                theme: 'dark',
                                onClick: () => (
                                    NewAnalyticsService.fireReferralExitEventBasedOnUrl(
                                        dylLanding.first5050CardCtaLink,
                                        fireEvents
                                    ),
                                    (window.location.href =
                                        dylLanding.first5050CardCtaLink)
                                ),
                            }}
                            mediaSide={
                                dylLanding.first5050CardImageSide === 'Left'
                                    ? 'top-left'
                                    : 'bottom-right'
                            }
                            backgroundColor={'white'}
                        />
                    </div>
                )}

            {!!(
                eligibleModelData &&
                !eligibleModelData.hideHTV &&
                vehicleAttributes?.year &&
                vehicleAttributes?.make &&
                vehicleAttributes?.marketName
            ) && (
                <HowToVideoLibrary
                    vehicleAttributes={{
                        year: vehicleAttributes?.year,
                        make: vehicleAttributes?.make,
                        model: vehicleAttributes?.marketName,
                        seoKey: vehicleAttributes.seoKey,
                    }}
                    seoKey={vehicleAttributes.seoKey}
                    origin={''}
                    vin={vin}
                />
            )}

            {eligibleModelData?.eligibleDylBlacklabel &&
                dylLandingDynamic &&
                dylLandingDynamic.blackLabelTitle && (
                    <div className="dyf-black-label-section">
                        <BlackLabel
                            mobileBackgroundImage={
                                aemBaseUrl +
                                dylLandingDynamic.blackLabelMobileImage
                            }
                            desktopBackgroundImage={
                                aemBaseUrl +
                                dylLandingDynamic.blackLabelDesktopImage
                            }
                            title={dylLandingDynamic.blackLabelTitle}
                            bodyContent={dylLandingDynamic.blackLabelBody}
                            buttonText={dylLandingDynamic.blackLabelButtonLabel}
                            buttonLink={dylLandingDynamic.blackLabelButtonLink}
                        />
                    </div>
                )}
            {/* Access Rewards */}
            {eligibleModelData && dylLanding && dylLanding.second5050CardTitle && (
                <div className="dyl-card-5050">
                    <CardFiftyFifty
                        title={TrademarkSymbolUtil.replaceTrademarks(
                            dylLanding.second5050CardTitle
                        )}
                        body={TrademarkSymbolUtil.replaceTrademarks(
                            dylLanding.second5050CardBody
                        )}
                        media={{
                            type: 'image',
                            width: 'wide',
                            src: aemBaseUrl + dylLanding.second5050CardImage,
                            alt: dylLanding.second5050CardImageAltText,
                        }}
                        button={{
                            type: 'standard',
                            label: dylLanding.second5050CardCtaLabel,
                            outlined: false,
                            chevron: 'right',
                            disabled: false,
                            theme: 'dark',
                            onClick: () =>
                                handleOnclick(dylLanding.second5050CardTitle),
                        }}
                        mediaSide={
                            dylLanding.second5050CardImageSide === 'Left'
                                ? 'top-left'
                                : 'bottom-right'
                        }
                        backgroundColor={'white'}
                    />
                </div>
            )}
            {/* Owner Manuals */}
            {!!(
                eligibleModelData &&
                vehicleAttributes?.year &&
                dylLandingDynamic &&
                dylLandingDynamic.third5050CardTitle
            ) && (
                <div className="dyl-card-5050">
                    <CardFiftyFifty
                        title={TrademarkSymbolUtil.replaceTrademarks(
                            dylLandingDynamic.third5050CardTitle
                        )}
                        body={TrademarkSymbolUtil.replaceTrademarks(
                            dylLandingDynamic.third5050CardBody
                        )}
                        media={{
                            type: 'image',
                            width: 'wide',
                            src:
                                aemBaseUrl +
                                dylLandingDynamic.third5050CardImage,
                            alt: dylLandingDynamic.third5050CardImageAltText,
                        }}
                        button={{
                            type: 'standard',
                            label: dylLandingDynamic.third5050CardCtaLabel,
                            outlined: false,
                            chevron: 'right',
                            disabled: false,
                            theme: 'dark',
                            onClick: () =>
                                handleOnclick(
                                    dylLandingDynamic.third5050CardTitle
                                ),
                        }}
                        mediaSide={'bottom-right'}
                        backgroundColor={'white'}
                    />
                </div>
            )}

            {/* Concierge */}
            {eligibleModelData && dylLanding && dylLanding.fourth5050CardTitle && (
                <div className="dyl-card-5050 dyl-last-component">
                    <CardFiftyFifty
                        title={TrademarkSymbolUtil.replaceTrademarks(
                            dylLanding.fourth5050CardTitle
                        )}
                        body={TrademarkSymbolUtil.replaceTrademarks(
                            dylLanding.fourth5050CardBody
                        )}
                        media={{
                            type: 'image',
                            width: 'wide',
                            src: aemBaseUrl + dylLanding.fourth5050CardImage,
                            alt: dylLanding.fourth5050CardImageAltText,
                        }}
                        button={{
                            type: 'standard',
                            label: dylLanding.fourth5050CardCtaLabel,
                            outlined: false,
                            chevron: 'right',
                            disabled: false,
                            theme: 'dark',
                            onClick: () => (
                                NewAnalyticsService.fireReferralExitEventBasedOnUrl(
                                    dylLanding.fourth5050CardCtaLink,
                                    fireEvents
                                ),
                                (window.location.href =
                                    dylLanding.fourth5050CardCtaLink)
                            ),
                        }}
                        mediaSide={
                            dylLanding.fourth5050CardImageSide === 'Left'
                                ? 'top-left'
                                : 'bottom-right'
                        }
                        backgroundColor={'white'}
                    />
                </div>
            )}
        </div>
    );
};
